import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "depth-chart__slider-wrapper" }
const _hoisted_2 = { class: "depth-chart__zoom" }

import { Application as PixiApplication, Cache as PixiCache } from 'pixi.js-legacy';
import ResizeObserver from 'resize-observer-polyfill';
import { computed, onBeforeUnmount, onMounted, ref, toRefs, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import numberFormat from '@exchange/helpers/number-format';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { OrderbookSide, useMyOrdersIndicators } from '@exchange/libs/orderbook/service/src';
import type { Orderbook } from '@exchange/libs/orderbook/service/src';
import { pixi } from '@exchange/libs/utils/pixi/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';

import Chart from './components/pixi-chart';
import {
  MIN_STARTING_ZOOM,
  MAX_POSSIBLE_ZOOM,
  MIN_POSSIBLE_ZOOM,
  FPS_BASE,
  FPS_DURING_HOVER,
  MIN_DCH_PL_NUMBER,
  getPricelevelsWithSum,
  getMinZoomPercent,
  getStartingZoomPercent,
  calcMidPrice,
  clamp,
} from './helpers';
import type { DataPointModel, Zoom } from './interfaces';

const startIndexZoomPercent = 10;

export default /*#__PURE__*/_defineComponent({
  __name: 'DepthChart',
  props: {
  amountPrecision: { type: Number, required: true },
  marketBaseCode: { type: String, required: true },
  marketId: { type: String, required: true },
  marketPrecision: { type: Number, required: true },
  orderbook: { type: Object as PropType<Orderbook>, required: true },
},
  emits: ["renderer-ready"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { marketId, orderbook } = toRefs(props);
const { locale } = useI18n({ useScope: 'global' });
const { myOrderIndicators } = useMyOrdersIndicators({ orderbook, marketId });

const el = ref<HTMLDivElement>();
const pixiContainerEl = ref<HTMLElement>();
const sliderDisabled = ref(true);
const zoom = ref<Zoom>({
  // all % value with 2 precision
  min: MIN_POSSIBLE_ZOOM,
  max: MAX_POSSIBLE_ZOOM,
  interval: 0.1,
  window: MIN_STARTING_ZOOM,
});
let dynamicTargetFPS = 5; // gets updated to 30 on pointerenter, 5 on pointerleave

let pixiApp: PixiApplication;
let pixiChart: Chart;

const askData = computed(() => {
  if (!orderbook.value) {
    return [];
  }

  return getPricelevelsWithSum(orderbook.value.orderbookProcessor.asks.priceLevels, OrderbookSide.ASK);
});
const bidData = computed(() => {
  if (!orderbook.value) {
    return [];
  }

  return getPricelevelsWithSum(orderbook.value.orderbookProcessor.bids.priceLevels, OrderbookSide.BID);
});

const resetZoom = () => {
  const firstAsk = askData.value[0]?.price ?? 0;
  const firstBid = bidData.value[0]?.price ?? 0;
  const midPrice = calcMidPrice(firstAsk, firstBid);

  zoom.value.min = getMinZoomPercent(midPrice, firstAsk, firstBid);
  zoom.value.window = getStartingZoomPercent(midPrice, askData.value[startIndexZoomPercent]?.price, bidData.value[startIndexZoomPercent]?.price);
};

const itemClicked = ([askPosition, bidPosition]: [DataPointModel, DataPointModel]): void => {
  const item = askPosition.isAskHovered ? askPosition : bidPosition;

  const orderType = orderformService.data.type;

  if (orderType !== OrderType.MARKET && item?.price && Math.sign(+item.price) === 1) {
    orderformService.setStopPrice(item.price.toString());
    orderformService.setLimitPrice(item.price.toString());
  }
};

const getFormattedNumber = (value: number, isMarketPrecision = true): string => {
  if (isMarketPrecision) {
    return numberFormat({
      number: value,
      precision: props.marketPrecision,
      locale: locale.value as string,
    });
  }

  return `${numberFormat({
    number: value,
    precision: Math.min(2, props.amountPrecision),
    locale: locale.value as string,
  })} ${props.marketBaseCode}`;
};

const handlePointerEnter = () => {
  dynamicTargetFPS = FPS_DURING_HOVER;
};
const handlePointerLeave = () => {
  dynamicTargetFPS = FPS_BASE;
};

const tickerFunc = (app: PixiApplication, chart: Chart) => () => {
  // eslint-disable-next-line no-param-reassign
  app.ticker.maxFPS = dynamicTargetFPS;
  let askTotal = 0;
  let bidTotal = 0;

  askData.value.forEach(({ amount }) => {
    askTotal += amount;
  });
  bidData.value.forEach(({ amount }) => {
    bidTotal += amount;
  });

  const firstAsk = askData.value[0]?.price ?? 0;
  const firstBid = bidData.value[0]?.price ?? 0;
  const midPrice = calcMidPrice(firstAsk, firstBid);

  sliderDisabled.value = askData.value.length <= MIN_DCH_PL_NUMBER || bidData.value.length <= MIN_DCH_PL_NUMBER;

  zoom.value.min = getMinZoomPercent(midPrice, firstAsk, firstBid);
  zoom.value.window = clamp(zoom.value.min, zoom.value.window, MAX_POSSIBLE_ZOOM);

  chart.refresh({
    askData: askData.value,
    bidData: bidData.value,
    askTotal,
    bidTotal,
    lastMatchedPrice: midPrice,
    zoomWindow: zoom.value.window,
    myOrders: myOrderIndicators.value,
  });

  zoom.value.window = chart.zoom.window;
};

const createAndAppendApp = (parentContainer: HTMLElement) => {
  pixiApp = new PixiApplication({
    autoDensity: true,
    backgroundColor: themingInstance.getColor('--v-theme-elevation-1'),
    resolution: 2,
    resizeTo: parentContainer,
    clearBeforeRender: true,
  });

  pixiApp.ticker.minFPS = 1;

  // @ts-ignore pixi
  parentContainer.appendChild(pixiApp.view);

  return {
    pixiApp,
  };
};

const createChart = (app: PixiApplication, parentContainer: HTMLElement, fontName: string) => {
  pixiChart = new Chart({
    canvas: app.view,
    zoom: zoom.value,
    orderFormClick: itemClicked,
    getFormattedNumber,
    askData: askData.value,
    bidData: bidData.value,
    cHeight: parentContainer.offsetHeight,
    cWidth: parentContainer.offsetWidth,
    fontName,
  });

  return {
    pixiChart,
  };
};

const startPixi = async (parentContainer: HTMLElement, fontName: string) => {
  sliderDisabled.value = true;
  await PixiCache.get(fontName);

  resetZoom();

  ({ pixiApp } = createAndAppendApp(parentContainer));
  ({ pixiChart } = createChart(pixiApp, parentContainer, fontName));

  pixiApp.stage.addChild(pixiChart);
  pixiApp.ticker.add(tickerFunc(pixiApp, pixiChart));
};

const resizeObserver = new ResizeObserver((resizeEvents: Array<ResizeObserverEntry>) => {
  resizeEvents.forEach(() => {
    const pixiContainer = pixiContainerEl.value;

    if (pixiContainer && pixiApp && pixiChart) {
      const { offsetWidth, offsetHeight } = pixiContainer;

      resetZoom();

      pixiApp.view.width = offsetWidth;
      pixiApp.view.height = offsetHeight;
      pixiApp.resize();

      pixiChart.resize({
        cHeight: offsetHeight,
        cWidth: offsetWidth,
      });
    }
  });
});

const tooltipFormatter = (value: number) => `${value.toFixed(2)}%`;

onMounted(async () => {
  const fontToLoad = themingInstance.appTheme.getCurrentThemeConfig().font['fnt-file-name'];
  const fontName = await pixi.initializeAwait(fontToLoad);
  const pixiContainer = pixiContainerEl.value;

  if (!pixiContainer) {
    logger.error('No container to append pixi');
    return;
  }

  if (!fontName) {
    logger.error('Font is not defined');
    return;
  }

  startPixi(pixiContainer, fontName)
    .then(() => {
      emit('renderer-ready', true);
    })
    .catch((e) => {
      logger.error('Start pixi failed', e);
    });

  if (el.value) {
    el.value.addEventListener('pointerenter', handlePointerEnter);
    el.value.addEventListener('pointerleave', handlePointerLeave);
    resizeObserver.observe(el.value);
  }
});

onBeforeUnmount(() => {
  if (el.value) {
    el.value.removeEventListener('pointerenter', handlePointerEnter);
    el.value.removeEventListener('pointerleave', handlePointerLeave);
    resizeObserver.disconnect();
  }

  if (pixiApp) {
    pixiApp.destroy(true);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_x_slider = _resolveComponent("x-slider")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "el",
    ref: el,
    class: _normalizeClass(["depth-chart", { 'depth-chart--disabled': sliderDisabled.value }])
  }, [
    _createElementVNode("div", {
      id: "pixi-container",
      ref_key: "pixiContainerEl",
      ref: pixiContainerEl,
      class: _normalizeClass('depth-chart__canvas')
    }, null, 512),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, _toDisplayString(Number.isNaN(zoom.value.window) ? 'N/A' : zoom.value.window.toFixed(2) + '%'), 1),
      _createVNode(_component_x_slider, {
        class: "depth-chart__slider",
        "model-value": zoom.value.window,
        min: zoom.value.min,
        max: zoom.value.max,
        interval: zoom.value.interval,
        silent: true,
        "step-style": { width: '6px', height: '6px', top: '-1px', left: '-1px' },
        duration: 0.2,
        "tooltip-formatter": tooltipFormatter,
        "drag-on-click": true,
        disabled: sliderDisabled.value,
        onChange: _cache[0] || (_cache[0] = ($event: any) => (zoom.value.window = $event))
      }, null, 8, ["model-value", "min", "max", "interval", "disabled"])
    ])
  ], 2))
}
}

})